import type { Inbox } from '../api/types';
import { channelHealth, identityLine, isManagedInbox } from './channelMeta';

/**
 * The DEFAULT SMS SENDER as the console shows it: the one number automated SMS
 * — the texts the dispatch system sends through the CRM — goes out from. The
 * server owns every rule (services/smsDefault.js); this only words what the
 * inbox list already says, so a status line, a question and a badge cannot come
 * to disagree about who the default is.
 */

const PROVIDER_LABEL: Partial<Record<Inbox['channelType'], string>> = {
  ringcentral: 'RingCentral',
  twilio: 'Twilio',
};

const providerOf = (inbox: Inbox) => PROVIDER_LABEL[inbox.channelType] ?? inbox.channelType;

export type SmsDefaultStatus =
  /** No inbox could be the default — there is nothing to say. */
  | { kind: 'none' }
  /** Numbers that could be exist, and none is: automated SMS is off. */
  | { kind: 'off' }
  | { kind: 'set'; inbox: Inbox }
  /** The default exists but cannot send — every automated text is failing. */
  | { kind: 'broken'; inbox: Inbox };

/**
 * Where automated SMS stands, read off one inbox list. A server that predates
 * the default answers neither field, which reads as `none` — never as `off`,
 * which would tell every tenant their texts had stopped.
 */
export function smsDefaultStatus(inboxes: Inbox[]): SmsDefaultStatus {
  const current = inboxes.find((inbox) => inbox.defaultSmsSender === true);
  if (current) {
    return channelHealth(current) === 'connected' ? { kind: 'set', inbox: current } : { kind: 'broken', inbox: current };
  }
  return inboxes.some((inbox) => inbox.smsDefaultEligible === true) ? { kind: 'off' } : { kind: 'none' };
}

/** "+1 516… (via RingCentral)" — how every question and line names a sender. */
export function senderName(inbox: Inbox): string {
  return `${identityLine(inbox)} (via ${providerOf(inbox)})`;
}

/** The status line above a channel list, or null when there is nothing to say. */
export function smsDefaultLine(status: SmsDefaultStatus): string | null {
  switch (status.kind) {
    case 'none':
      return null;
    case 'off':
      return 'Automated SMS: off. Choose a number.';
    case 'set':
      return `Automated SMS: sent from ${identityLine(status.inbox)} via ${providerOf(status.inbox)}`;
    case 'broken':
      return `Your default SMS sender ${senderName(status.inbox)} needs attention — automated SMS is failing.`;
  }
}

/** The question asked when another number would take over, in the agreed words. */
export function replaceQuestion(current: Inbox, nextIdentity: string): string {
  return `${senderName(current)} is your current default sender for automated SMS. Do you want to replace it with ${nextIdentity}?`;
}

/** The providers a default SMS sender can be on. */
export type SmsSenderType = 'ringcentral' | 'twilio';

export interface SmsSenderOption {
  inbox: Inbox;
  /** "+12125550199 · Front desk SMS": the number first, because it is what a customer's phone shows. */
  label: string;
  /** Why it cannot be picked right now, or null when it can. */
  blocked: string | null;
}

/**
 * What the Automated SMS form offers under each provider: every inbox the
 * server says could be the default, in list order. One that cannot take the job
 * right now stays listed, blocked with the reason, so the operator sees why it
 * cannot be picked instead of wondering where it went — the server would refuse
 * it anyway (inboxCont.usableSender: manage access, then a channel that can send).
 */
export function smsSenderOptions(inboxes: Inbox[]): Record<SmsSenderType, SmsSenderOption[]> {
  const options: Record<SmsSenderType, SmsSenderOption[]> = { ringcentral: [], twilio: [] };
  for (const inbox of inboxes) {
    if (inbox.smsDefaultEligible !== true) continue;
    if (inbox.channelType !== 'ringcentral' && inbox.channelType !== 'twilio') continue;
    const blocked = !isManagedInbox(inbox)
      ? 'needs Manage access'
      : channelHealth(inbox) !== 'connected'
        ? 'reconnect first'
        : null;
    options[inbox.channelType].push({ inbox, label: `${identityLine(inbox)} · ${inbox.name}`, blocked });
  }
  return options;
}

/**
 * What could take over from `excludeId` when it is disconnected: the other
 * inboxes that could be a default and can send right now. One that cannot send
 * is left out rather than offered — the server refuses it, and only after the
 * operator had already made their choice.
 */
export function replacementChoices(inboxes: Inbox[], excludeId: number): Inbox[] {
  return inboxes.filter(
    (inbox) =>
      inbox.id !== excludeId && inbox.smsDefaultEligible === true && channelHealth(inbox) === 'connected',
  );
}
